import { initializeApp } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  getAuth,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
  sendPasswordResetEmail,
  sendEmailVerification,
  User,
  updateEmail,
  updateProfile,
  signInWithPopup,
  signInWithCustomToken,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  connectAuthEmulator,
} from 'firebase/auth';
import { DocumentReference, DocumentSnapshot, FieldValue, Timestamp, Unsubscribe, addDoc, collection, connectFirestoreEmulator, doc, getDoc, getFirestore, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import {
  connectStorageEmulator,
  getDownloadURL,
  getMetadata,
  getStorage,
  ref,
  uploadBytes,
} from 'firebase/storage';
import { type Analytics, type EventNameString, getAnalytics, isSupported as isAnalyticsSupported, logEvent, setUserId } from 'firebase/analytics';
import { COLLECTION, NEEDS_ATTENTION_STATUSES, SUBCOLLECTION, SubscriptionProvider, SubscriptionStatus, UserData, UserRoles } from '@amplio/contracts';
import { useStore } from './src/store/store';
import { firebaseConfig } from './src/helpers/firebase-config';

// Re-exported so the rest of the site keeps importing the shared vocabulary
// from here, as it always has. The definitions live in contracts/data/ and are
// generated into @amplio/contracts for the tool, the backend and this app; the
// player gets the Kotlin equivalents from the same source.
export { UserRoles };
export type { SubscriptionProvider, SubscriptionStatus };

export let pendingAuthState = useStore.getState().userPreviouslyLoggedIn;

/**
 * Whether the user is entitled to Plus.
 *
 * The `plus` role is the single source of truth: it is what gates Plus features
 * everywhere, and the backend grants/revokes it from each provider's
 * authoritative state via setPlusEntitlement. Gating the upgrade path on
 * anything else means the nav and the features can disagree — which is exactly
 * what happened when this read `subscriptionStatus !== 'canceled'` and so
 * treated an expired Google subscription as current.
 */
export function hasPlusEntitlement(userData: UserDataType | null | undefined): boolean {
  return !!userData?.roles?.includes(UserRoles.Plus);
}

/**
 * Whether an entitled user's subscription has a billing problem worth warning
 * them about. Purely advisory — it never gates access, and it is only meaningful
 * for a user who still holds the role (see hasPlusEntitlement).
 */
export function subscriptionNeedsAttention(provider: SubscriptionProvider | undefined,
                                           status: SubscriptionStatus | undefined): boolean {
  if (!provider || !status) {
    return false;
  }
  return NEEDS_ATTENTION_STATUSES[provider]?.has(status) ?? false;
}

/**
 * The `users/{uid}` document, generated from
 * contracts/data/user-data.schema.json — the same declaration the tool and the
 * Cloud Functions use, parameterised with this app's own `Timestamp` class.
 */
export type UserDataType = UserData<Timestamp>;


const firebaseApp = initializeApp(firebaseConfig);
export const auth = getAuth(firebaseApp);
export const firestore = getFirestore(firebaseApp);
const cloudStorage = getStorage(firebaseApp);
const functions = getFunctions(firebaseApp);
let analytics: Analytics | null = null;
isAnalyticsSupported().then((isSupported) => {
  if (isSupported) {
    analytics = getAnalytics(firebaseApp);
  }
});

if (process.env.USE_EMULATOR === '1') {
  connectAuthEmulator(auth, 'http://localhost:9099');
  connectFirestoreEmulator(firestore, 'localhost', 8080);
  connectFunctionsEmulator(functions, 'localhost', 5001);
  connectStorageEmulator(cloudStorage, 'localhost', 9199);
}

const googleProvider = new GoogleAuthProvider();

const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

export const loginUser = async (loginEmail: string, loginPassword: string) => {
  const userCredential = await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
  await getAndCacheLatestUserData(userCredential.user);
  checkUserSetup();
  return userCredential.user;
};

export const reAuthenticateUserWithPassword = async (loginPassword: string) => {
  await reauthenticateWithCredential(auth.currentUser, EmailAuthProvider.credential(auth.currentUser.email, loginPassword));
  useStore.setState({ needsReauthentication: false });
};

export const loginUserWithGoogle = async () => {
  const userCredential = await signInWithPopup(auth, googleProvider);
  await getAndCacheLatestUserData(userCredential.user);
  checkUserSetup();
  return userCredential.user;
};

export const signUpUserWithGoogle = async () => {
  const userCredential = await signInWithPopup(auth, googleProvider);
  return userCredential.user;
};

export const loginUserWithApple = async () => {
  const userCredential = await signInWithPopup(auth, appleProvider);
  await getAndCacheLatestUserData(userCredential.user);
  checkUserSetup();
  return userCredential.user;
};

export const signUpUserWithApple = async () => {
  const userCredential = await signInWithPopup(auth, appleProvider);
  return userCredential.user;
};

export const signUpUser = async (loginEmail: string, loginPassword: string) => {
  const userCredential = await createUserWithEmailAndPassword(auth, loginEmail, loginPassword);
  return userCredential.user;
};

export const updateUserEmail = async (user: User, newEmail: string) => {
  try {
    await updateEmail(user, newEmail);
  } catch (error) {
    if (error.code === 'auth/requires-recent-login') {
      useStore.setState({
        needsReauthentication: true,
        reasonToLogIn: 'It has been too long since you last signed in. '
          + 'Please sign in with your old email address to make updates your email address',
        showLoginPopup: true,
      });
    }
    throw error;
  }
  await verifyEmail(user);
};

export const updateUserPhotoUrl = async (photoURL: string): Promise<void> => {
  const user = auth.currentUser;
  if (user) {
    return updateProfile(user, {
      photoURL,
    });
  }
  throw new Error('No user signed in');
};

export const updateUserPassword = async (user: User, newPassword: string) => {
  await updatePassword(user, newPassword);
};

export const updatePartialUserData = async (partialUserData: Partial<UserDataType>) => {
  if (!auth?.currentUser) {
    return;
  }
  const docRef = doc(firestore, 'users', auth.currentUser.uid);
  await setDoc(docRef, partialUserData, { merge: true });
  useStore.setState((state) => ({
    userData: {
      ...state.userData,
      ...partialUserData,
    },
  }));
};

export const updateUserData = async (user: User, userData: UserDataType) => {
  if (!user) {
    return;
  }
  const docRef = doc(firestore, 'users', user.uid);
  await setDoc(docRef, userData);
  useStore.setState({ userData });
};

export const getAndCacheLatestUserData = async (user: User): Promise<UserDataType | null> => {
  let docRef;
  try {
    docRef = doc(firestore, 'users', user.uid);
  } catch (e) {
    console.error('got an error trying to get the user doc ref', e);
    return null;
  }
  const docSnap = await getDoc(docRef) as DocumentSnapshot<UserDataType>;
  const userDataExists = docSnap.exists();
  const userData = docSnap.data();
  if (userDataExists) {
    // Ensure the email on file matches the email used to sign up
    if (userData.email !== user.email) {
      userData.email = user.email;
      await updateUserData(user, userData);
    }
    useStore.setState({ userData });
    return userData;
  }
  return null;
};

export const resetPassword = async (email: string) => {
  await sendPasswordResetEmail(auth, email);
};

export const verifyEmail = async (user: User, continueUrl?: string) => {
  try {
    await sendEmailVerification(user, { url: continueUrl });
  } catch (error) {
    if (error.code === 'auth/too-many-requests') {
      console.error('You have tried to send too many verification emails. Please try again later');
    }
  }
};

export const signOutUser = async () => {
  useStore.setState({ userData: null });
  await auth.signOut();
};

// The URL fragment key the mobile app uses to pass a single-use login nonce,
// e.g. https://ampliomusic.com/manage-account#loginNonce=<nonce>. We read from
// the fragment (not a query string) so the nonce is never sent to the server or
// leaked via the Referer header to third-party resources on the page.
const LOGIN_NONCE_HASH_KEY = 'loginNonce';

// Upper bound on how long the overlay waits for the first user-data snapshot
// after a successful sign-in. The session is already valid at that point, so
// timing out drops to 'idle' rather than 'failed' and lets the ordinary
// hydration gates take it from there — the pre-existing behaviour.
const USER_DATA_SETTLE_TIMEOUT_MS = 15_000;

const waitForUserDataToSettle = async () => {
  if (useStore.getState().userData !== null) {
    return;
  }
  await new Promise<void>((resolve) => {
    const finish = () => {
      clearTimeout(timeout);
      unsubscribe();
      resolve();
    };
    const timeout = setTimeout(finish, USER_DATA_SETTLE_TIMEOUT_MS);
    const unsubscribe = useStore.subscribe((state) => {
      if (state.userData !== null) {
        finish();
      }
    });
  });
};

/**
 * If the current URL carries a login nonce in its fragment, redeem it for a
 * session. Intended to run once, as early as possible on client entry.
 *
 * Order matters for security:
 *  1. Read the nonce from location.hash.
 *  2. Immediately strip it from the URL (history.replaceState) so it does not
 *     linger in history, get copied, or leak via Referer to later requests.
 *  3. Sign out any existing user, so we never briefly run listeners/writes as
 *     the wrong account and a failed redemption cannot leave a stale session.
 *  4. Exchange the nonce for a custom token server-side and sign in with it.
 *
 * The nonce alone is worthless outside redeemLoginNonce, so even if it leaks in
 * step 1's window it cannot be exchanged directly against Firebase.
 *
 * Everything up to the first await runs synchronously, so loginNonceStatus is
 * set to 'redeeming' before Gatsby renders and access gates never see a
 * "signed out" state they would have to take back a second later.
 */
export const redeemLoginNonceFromUrl = async (): Promise<boolean> => {
  if (typeof window === 'undefined' || !window.location.hash) {
    return false;
  }

  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const nonce = hashParams.get(LOGIN_NONCE_HASH_KEY);
  if (!nonce) {
    return false;
  }

  // Strip the nonce from the URL before doing anything else.
  hashParams.delete(LOGIN_NONCE_HASH_KEY);
  const remainingHash = hashParams.toString();
  const cleanedUrl = window.location.pathname + window.location.search
    + (remainingHash ? `#${remainingHash}` : '');
  window.history.replaceState(null, '', cleanedUrl);

  useStore.setState({ loginNonceStatus: 'redeeming' });
  try {
    // Clear any existing session first. Firebase restores a persisted session
    // asynchronously, so at client entry auth.currentUser is still null even
    // when there is one — checking it directly skipped the sign-out and let the
    // previous account land mid-redemption, briefly signing the user in as the
    // wrong one (and, since that populates userData, dropping the overlay early).
    await auth.authStateReady();
    if (auth.currentUser) {
      await signOutUser();
    }
    const { data } = await redeemLoginNonce({ nonce });
    await signInWithCustomToken(auth, data.token);
    // signInWithCustomToken resolving only means Firebase accepted the token.
    // The onAuthStateChanged listener below then sets userPreviouslyLoggedIn
    // synchronously, but userData arrives later still, on the first Firestore
    // snapshot. Clearing the overlay in between is what made the spinner jump:
    // isUserDataStillHydrating is still true, so the access gates put up their
    // own spinner — and theirs is laid out in the page flow, under a 10rem
    // PaddingBlock, rather than centred in the viewport. Waiting for the data
    // the page actually needs keeps one overlay up continuously, from hand-off
    // to rendered content.
    await waitForUserDataToSettle();
    useStore.setState({ loginNonceStatus: 'idle' });
    return true;
  } catch (error) {
    console.error('Failed to sign in from login nonce', error);
    // Left set rather than reset to 'idle': the overlay stays up with a
    // failure message instead of silently falling through to signed-out
    // content, until the user acts on it (see LoginNonceOverlay).
    useStore.setState({ loginNonceStatus: 'failed' });
    return false;
  }
};

export const deleteUserAccount = async () => {
  try {
    await handleAccountClosure();
  } catch (error) {
    if (error.code === 'auth/requires-recent-login') {
      useStore.setState({
        needsReauthentication: true,
        reasonToLogIn: 'It has been too long since you last signed in. '
          + 'Please sign in again before deleting your account',
        showLoginPopup: true,
      });
    }
    throw error;
  }
};

export const logAnalyticsEvent = (eventName: string | EventNameString,
                                  eventParams?: Record<string, unknown>): void => {
  if (analytics && !useStore.getState().userData?.roles?.includes(UserRoles.TeamMember)) {
    logEvent(analytics, eventName, eventParams);
  } else {
    console.log('Analytics not supported, logging event', eventName, eventParams);
  }
};

// Cloud Storage definitions
export const uploadFileForUser = async (file: File, path: string) => {
  const userFolderRef = ref(cloudStorage, `user-uploads/${auth.currentUser.uid}`);
  const fileRef = ref(userFolderRef, path);
  await uploadBytes(fileRef, file);
};

// Email header images. The returned download URL never expires and is readable
// without auth (see storage.rules) because mail clients fetch it long after the
// send. The object name is uniquified so a re-upload can never be served from a
// cached copy of the previous image. Date.now() alone is not enough: two
// uploads for the same draft in the same millisecond would collide and, under
// immutable caching, leave clients stuck with the first image for up to a year.
export const uploadEmailHeaderImage = async (draftKey: string, file: File): Promise<string> => {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '-');
  const unique = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const fileRef = ref(cloudStorage, `email-assets/${draftKey}/${unique}-${safeName}`);
  await uploadBytes(fileRef, file, {
    contentType: file.type,
    cacheControl: 'public, max-age=31536000, immutable',
  });
  return getDownloadURL(fileRef);
};

// Null rather than throwing: the size is shown alongside an image that is
// already attached, so not knowing it is worth a "size unknown" label, never
// an error on the draft.
export const getEmailHeaderImageSize = async (downloadUrl: string): Promise<number | null> => {
  try {
    const metadata = await getMetadata(ref(cloudStorage, downloadUrl));
    return typeof metadata.size === 'number' ? metadata.size : null;
  } catch {
    return null;
  }
};

export const getPublicUrlForUserFile = async (path: string) => {
  const userFolderRef = ref(cloudStorage, `user-uploads/${auth.currentUser.uid}`);
  const fileRef = ref(userFolderRef, path);
  return getDownloadURL(fileRef);
};

export interface AnswerMapEntry {
  question: Queries.ContentfulFormQuestion;
  answer?: string | string[] | null;
  index: number;
}

interface FormSubmission {
  userId: string;
  nameOfUser: string;
  userEmail: string;
  timestamp: FieldValue;
  answers: AnswerMapEntry[];
  sendTo: string[];
  isPaymentForm: boolean;
  approvalQueueId?: string;
}

interface ContactDetails {
  email: string;
  first_name?: string;
  last_name?: string;
  source?: string;
  country?: string;
  alternate_emails?: string[];
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state_province_region?: string;
  postal_code?: string;
  custom_fields?: {
    [key: string]: string;
    'deepDiver'?: 'true' | 'false';
    'hasAmplioAccount'?: 'true' | 'false';
    'hasDownloadedApp'?: 'true' | 'false';
    'isAccountSetup'?: 'true' | 'false';
    'isEngagedUser'?: 'true' | 'false';
    'isMarketingEmailApproved'?: 'true' | 'false';
    'addedFromLeadMagnet'?: string;
  };
}

interface UpsertContactsData {
  contacts: ContactDetails[];
  list_ids: string[];
}

interface SendLeadMagnetEmailData {
  name: string;
  email: string;
  leadMagnetName: string;
  emailTemplateId: string;
  downloadUrl: string;
  mailingListGroups?: string[];
}

interface TrackLeadMagnetDownloadData {
  leadMagnetName: string;
  mailingListGroups?: string[];
}

export interface AdminUpdateUserData {
  uid: string;
  updates: {
    email?: string;
    name?: string;
    country?: string;
    roles?: string[];
  };
  reason?: string;
}

export interface AdminUpdateUserResult {
  success: boolean;
  warnings: string[];
  user: Record<string, unknown>;
}

export interface AdminUserAuthStateResult {
  emailVerified: boolean;
  providerIds: string[];
  ssoProviders: string[];
  isSsoOnly: boolean;
}

interface AdminResendVerificationEmailResult {
  success: boolean;
  sent: boolean;
  message?: string;
}

export interface AdminDeleteUserData {
  uid: string;
  reason?: string;
}

export interface AdminDeleteUserResult {
  success: boolean;
  warnings: string[];
}

export type EmailDraftStatus = 'draft' | 'readyToSend' | 'scheduled' | 'sent' | 'failed';

export interface EmailDraft {
  id: string;
  name: string;
  subject: string;
  bodyMarkdown: string;
  headerImageUrl: string | null;
  groupIds: string[];
  status: EmailDraftStatus;
  senderCampaignId: string | null;
  scheduleTimeSent: string | null;
  createdBy: string;
  lastError: string | null;
  scheduledFor: number | null;
  removedFromSenderAt?: number | null;
  createdAt: number | null;
  updatedAt: number | null;
  sentAt: number | null;
  lastSendAttemptAt?: number | null;
  lastSendError?: string | null;
}

export interface SaveEmailDraftData {
  id?: string;
  name: string;
  subject: string;
  bodyMarkdown: string;
  headerImageUrl?: string | null;
  groupIds: string[];
}

export interface EmailGroup {
  id: string;
  title: string;
  activeSubscribers: number;
  recipientCount: number;
  unsubscribedCount: number;
  bouncedCount: number;
}

export interface ListEmailGroupsResult {
  groups: EmailGroup[];
  allMarketable: {
    id: string;
    strategy: 'segment';
    segmentId: string;
    activeSubscribers: number;
  };
  // Absent while the functions deploy predates it; null when the server's zone is unset.
  senderTimeZone?: string | null;
}

export interface SenderCampaign {
  id: string;
  title: string | null;
  subject: string | null;
  status: string | null;
  created: string | null;
  sentTime: string | null;
  // Absent when the deployed function predates these fields.
  createdMillis?: number | null;
  sentMillis?: number | null;
  recipientCount: number;
  sentCount: number;
  opens: number;
  clicks: number;
}

export type TosBatchStatus = 'seeding' | 'running' | 'paused' | 'cancelled' | 'finished';

export interface TosAudienceReport {
  usersScanned: number;
  noEmail: number;
  duplicateEmail: number;
  excludedBounce: number;
  excludedComplaint: number;
  excludedUnknown: number;
  queued: number;
  // Absent when this report was stored by a deploy before this field existed.
  marketingUnsubscribed?: number;
}

export interface TosBatchCounts {
  sent: number;
  remaining: number;
  failed: number;
  cancelled: number;
  total: number;
}

export interface TosBatchView {
  id: string;
  status: TosBatchStatus;
  subject: string;
  bodyMarkdown: string;
  effectiveDate: string;
  batchSize: number;
  audience: TosAudienceReport | null;
  counts: TosBatchCounts;
  createdByName: string | null;
  createdAt: number | null;
  startedAt: number | null;
  cancelledByName: string | null;
  cancelledAt: number | null;
  finishedAt: number | null;
  updatedAt: number | null;
}

export interface TosDrainStatus {
  forceEnabled: boolean;
  activeBatch: TosBatchView | null;
  lastBatch: TosBatchView | null;
  defaults: { subject: string; bodyMarkdown: string; batchSize: number };
  // Optional because an older functions deploy does not send it yet.
  savedDraft?: TosSavedDraft | null;
  batchSizeBounds: { min: number; max: number };
  maxAttempts: number;
  noticeLeadDays: number;
  audienceEstimate: TosAudienceReport | null;
  minimumEffectiveDate: string | null;
}

export interface TosSavedDraft {
  subject: string;
  bodyMarkdown: string;
  effectiveDate: string | null;
  batchSize: number;
  savedAtMillis: number | null;
}

export interface TosNoticeDraftInput {
  subject: string;
  bodyMarkdown: string;
  effectiveDate: string | null;
  batchSize: number;
}

export interface TosNoticeCopy {
  subject: string;
  bodyMarkdown: string;
  effectiveDate: string;
}

export interface TosNoticeBatchInput extends TosNoticeCopy {
  batchSize: number;
}

export interface PreviewTosNoticeBatchResult {
  subject: string;
  text: string;
  html: string;
  audience: TosAudienceReport;
  batchSize: number;
  minimumEffectiveDate: string;
  estimatedFinishDate: string | null;
}

export type TosBatchAction = 'pause' | 'resume' | 'setBatchSize' | 'setCopy' | 'cancel';

export interface UpdateTosNoticeBatchData {
  batchId: string;
  action: TosBatchAction;
  batchSize?: number;
  subject?: string;
  bodyMarkdown?: string;
}

export interface UpdateTosNoticeBatchResult {
  batch: TosBatchView | null;
  dateNoLongerSafe: boolean;
}

export interface RequestSiteRebuildResult {
  dispatched: boolean;
  /** Epoch ms from which another build may be dispatched. */
  nextBuildAt: number;
  message: string;
}

// Cloud function definitions
export const mintCustomToken =
  httpsCallable<{idToken: string}, {token: string}>(functions, 'mintCustomToken');
// Exchanges a single-use login nonce (from the mobile app's "Manage Account"
// hand-off) for a Firebase custom token, which we immediately redeem. The nonce
// travels in the URL; the custom token never does. See redeemLoginNonceFromUrl.
export const redeemLoginNonce =
  httpsCallable<{nonce: string}, {token: string}>(functions, 'redeemLoginNonce');
export const upsertContacts =
  httpsCallable<UpsertContactsData, unknown>(functions, 'upsertContacts');
export const sendLeadMagnetEmail =
  httpsCallable<SendLeadMagnetEmailData, unknown>(functions, 'sendLeadMagnetEmail');
export const trackLeadMagnetDownload =
  httpsCallable<TrackLeadMagnetDownloadData, unknown>(functions, 'trackLeadMagnetDownload');
export const handleAccountClosure =
  httpsCallable<Record<string, never>, unknown>(functions, 'handleAccountClosure');
// The only path by which an admin may change a user record: firestore.rules
// keeps /users admin-read-only so every write is validated and audited here.
export const adminUpdateUser =
  httpsCallable<AdminUpdateUserData, AdminUpdateUserResult>(functions, 'adminUpdateUser');
export const adminGetUserAuthState =
  httpsCallable<{ uid: string }, AdminUserAuthStateResult>(functions, 'adminGetUserAuthState');
export const adminResendVerificationEmail =
  httpsCallable<{ uid: string }, AdminResendVerificationEmailResult>(
    functions,
    'adminResendVerificationEmail',
  );
export const adminDeleteUser =
  httpsCallable<AdminDeleteUserData, AdminDeleteUserResult>(functions, 'adminDeleteUser');
// Fires the `song-publish` repository_dispatch that rebuilds both sites, from
// the Song Manager. Producer-gated and debounced server-side: the GitHub token
// stays in the function, and a burst of saves queues one build.
export const requestSiteRebuild =
  httpsCallable<void, RequestSiteRebuildResult>(functions, 'requestSiteRebuild');
export interface ResyncSongTagsResult {
  written: number;
  deleted: string[];
  pruneSkippedReason?: string;
}
// The Contentful webhook keeps song-tags current; this is the recovery path
// for a webhook that never arrived.
export const resyncSongTags =
  httpsCallable<void, ResyncSongTagsResult>(functions, 'resyncSongTags');
export const listEmailGroups =
  httpsCallable<undefined, ListEmailGroupsResult>(functions, 'listEmailGroups');
export const listSenderCampaigns =
  httpsCallable<undefined, { campaigns: SenderCampaign[] }>(functions, 'listSenderCampaigns');
export const listEmailDrafts =
  httpsCallable<undefined, { drafts: EmailDraft[] }>(functions, 'listEmailDrafts');
export const saveEmailDraft =
  httpsCallable<SaveEmailDraftData, EmailDraft>(functions, 'saveEmailDraft');
export const deleteEmailDraft =
  httpsCallable<{ id: string }, { success: true }>(functions, 'deleteEmailDraft');
export const previewEmailDraft =
  httpsCallable<{ id: string }, { html: string }>(functions, 'previewEmailDraft');
export const sendEmailDraftTest =
  httpsCallable<{ id: string }, { sentTo: string }>(functions, 'sendEmailDraftTest');
export const sendEmailDraft =
  httpsCallable<{ id: string; expectedUpdatedAt: number }, EmailDraft>(functions, 'sendEmailDraft');
export const scheduleEmailDraft =
  httpsCallable<{ id: string; expectedUpdatedAt: number; sendAt: string; timeZone: string }, EmailDraft>(
    functions,
    'scheduleEmailDraft',
  );
export const cancelScheduledEmail =
  httpsCallable<{ id: string }, EmailDraft>(functions, 'cancelScheduledEmail');
// T&C notice batches. Admin-only server-side; see tos-drain-admin.ts.
export const getTosDrainStatus =
  httpsCallable<Record<string, never>, TosDrainStatus>(functions, 'getTosDrainStatus');
export const previewTosNoticeBatch =
  httpsCallable<TosNoticeBatchInput, PreviewTosNoticeBatchResult>(functions, 'previewTosNoticeBatch');
export const sendTosNoticeTest =
  httpsCallable<TosNoticeCopy, { sentTo: string }>(functions, 'sendTosNoticeTest');
export const startTosNoticeBatch =
  httpsCallable<TosNoticeBatchInput, { batch: TosBatchView | null }>(functions, 'startTosNoticeBatch');
export const updateTosNoticeBatch =
  httpsCallable<UpdateTosNoticeBatchData, UpdateTosNoticeBatchResult>(functions, 'updateTosNoticeBatch');
export const saveTosNoticeDraft =
  httpsCallable<TosNoticeDraftInput, { savedDraft: TosSavedDraft | null }>(functions, 'saveTosNoticeDraft');
export const clearTosNoticeDraft =
  httpsCallable<Record<string, never>, { savedDraft: null }>(functions, 'clearTosNoticeDraft');

export const addFormAnswers = async (formId: string,
                                     answers: AnswerMapEntry[],
                                     sendToTheseEmails: string[],
                                     isPaymentForm=false,
                                     useEmail: string,
                                     useName?: string): Promise<string | null> => {
  const { userData } = useStore.getState();
  const nameOfUser = useName || userData?.name || auth?.currentUser?.displayName || 'Unknown Name';
  try {
    const formSubmissionSubCollection = collection(firestore, COLLECTION.Forms, formId, SUBCOLLECTION.FormSubmissions);
    const formSubmissionData: FormSubmission = {
      userId: auth?.currentUser?.uid || 'unknown',
      nameOfUser,
      userEmail: useEmail,
      timestamp: serverTimestamp(),
      answers,
      sendTo: sendToTheseEmails,
      isPaymentForm,
    };
    const formSubmissionRef = await addDoc(formSubmissionSubCollection, formSubmissionData);
    console.log('Successfully sent form');
    return formSubmissionRef.id;
  } catch (e) {
    console.error('Error saving document', e);
    return null;
  }
};

function checkReasonToLogin() {
  // Show the download popup if the user tried to download before signin
  const { reasonToLogIn, needsReauthentication } = useStore.getState();
  if (reasonToLogIn?.includes('download')) {
    useStore.setState({ reasonToLogIn: null, showDownloadPopup: true });
  } else if (reasonToLogIn && !needsReauthentication) {
    useStore.setState({ reasonToLogIn: null });
  }
}

function checkEmailIsVerified(user: User) {
  if (!user.emailVerified) {
    // TODO - this should be a popup allowing the user to resend the
    // verification email to their email address
    useStore.setState({ needsEmailVerification: true });
  } else {
    useStore.setState({ needsEmailVerification: false });
  }
}

async function checkUserSetup() {
  // Check that their account still needs setup
  const userData = useStore.getState().userData;
  if ((!userData || !userData.isAccountSetup)
      && (!useStore.getState().isInProcessOfSigningUp)) {
    // The user needs to finish setting up their account
    useStore.setState({ showNeedsAccountSetupDialog: true });
    return;
  }
}

// Check on the user's account when their auth state changes
let unsubscribeFromUserDataUpdates: Unsubscribe | null = null;

auth.onAuthStateChanged(async (updatedUser) => {
  pendingAuthState = false;

  // Keep the live auth user in the store so components can read it
  // synchronously and it survives page navigation (see useUser). This is the
  // single source of truth for the current Firebase user.
  useStore.setState({ user: updatedUser });

  // Make sure we only have one listener hanging around, so clean this up first
  // if it already exists
  if (unsubscribeFromUserDataUpdates) {
    unsubscribeFromUserDataUpdates();
    unsubscribeFromUserDataUpdates = null;
  }

  if (updatedUser) {
    // Any time the user's data updates, we want to make sure the updated copy
    // is saved in the store
    let docRef: DocumentReference<UserDataType>;
    try {
      docRef = doc(firestore, 'users', updatedUser.uid) as DocumentReference<UserDataType>;
      unsubscribeFromUserDataUpdates = onSnapshot(docRef, (doc) => {
        const userData = doc.data();
        // Ensure we freeze the object coming from firebase so that it can't be
        // modified by the client
        useStore.setState({ userData: Object.freeze(userData) });
        checkUserSetup();
      });
    } catch (e) {
      console.error('Could not retrieve any user data', e);
    }
    checkEmailIsVerified(updatedUser);

    if (analytics) {
      setUserId(analytics, updatedUser.uid);
    }

    // Check if we need to redirect anywhere after checking this
    checkReasonToLogin();

    useStore.setState({ userPreviouslyLoggedIn: true, needsReauthentication: false });
  } else {
    useStore.setState({
      userData: null,
      userPreviouslyLoggedIn: false,
      needsReauthentication: false,
      reasonToLogIn: null,
      needsEmailVerification: false,
    });
  }
});
